import { checkpointsPassed, deriveRouteLabel } from './lib/cockpitTruth.mjs';
import { coordinationEnvelope, stableIdempotencyKey } from '../functions/_shared/backroom-envelope.mjs';

const getApiBaseUrl = () => {
  if (typeof window !== 'undefined') return '/api';
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

const fetchFresh = async (url, options = {}) => {
  const separator = url.includes('?') ? '&' : '?';
  const freshUrl = `${url}${separator}_t=${Date.now()}`;
  const init = {
    ...options,
    // Same-origin proxies + Teleporte; avoid stale caches without forbidden headers
    cache: 'no-store',
  };
  try {
    return await fetch(freshUrl, init);
  } catch (first) {
    // One retry for flaky edge / "Failed to fetch"
    await new Promise((r) => setTimeout(r, 400));
    return fetch(freshUrl, init);
  }
};

/** Safe JSON parse — HTML SPA fallback must not throw pattern errors. */
const safeJson = async (response) => {
  const text = await response.text();
  const trimmed = String(text || '').trim();
  if (!trimmed) return { ok: false, code: 'not_loaded', error: 'empty body', data: null };
  if (trimmed.startsWith('<') || trimmed.toLowerCase().startsWith('<!doctype')) {
    return {
      ok: false,
      code: 'not_loaded',
      error: 'HTML response (no JSON API route)',
      data: null,
    };
  }
  try {
    return { ok: true, data: JSON.parse(trimmed) };
  } catch (e) {
    return {
      ok: false,
      code: 'not_loaded',
      error: e.message || 'JSON parse failed',
      data: null,
    };
  }
};

export const normalizeCurrentRun = (data = {}) => {
  if (!data || typeof data !== 'object') return { success: false };
  const mission = data.mission && typeof data.mission === 'object' ? data.mission : {};
  const canonical = data.canonical_current_state && typeof data.canonical_current_state === 'object'
    ? data.canonical_current_state
    : {};
  const status = mission.status || data.status || 'active';
  const phase = mission.phase || canonical.phase || data.phase || null;
  const goal = mission.goal || mission.end_goal || data.goal || data.objective || null;
  const next_action = mission.next_action || canonical.next_action || data.next_action || null;
  const blockers = Array.isArray(mission.blockers)
    ? mission.blockers
    : Array.isArray(data.blockers)
      ? data.blockers
      : [];
  return {
    ...data,
    success: data.ok !== false && data.success !== false,
    goal,
    objective: goal || data.objective || null,
    status,
    phase,
    next_action,
    blockers,
    human_owner: mission.human_owner || data.human_owner || 'operator',
    owner_agent: mission.owner_agent || data.owner_agent || null,
    final_authority: mission.final_authority || data.final_authority || 'operator',
    check_in_required: mission.check_in_required === true || data.check_in_required === true,
    final_report_allowed: mission.final_report_allowed,
    revision: canonical.revision ?? data.revision ?? null,
    canonical_revision: canonical.revision ?? data.canonical_revision ?? null,
    health: canonical.health || data.health || null,
  };
};

export const normalizeMissionBoard = (data = {}) => {
  if (!data || typeof data !== 'object') return data;
  const signals = data.status_code_signals || {};
  const status = data.status || signals.STATUS || 'active';
  const phase =
    data.phase ||
    signals.CURRENT_PHASE ||
    data.CURRENT_PHASE ||
    null;
  const goal = data.goal || data.BIG_PICTURE || data.ACTIVE_MISSION || null;
  const next =
    data.next_action ||
    data.NEXT_ACTION ||
    null;
  return {
    ...data,
    success: data.ok !== false && data.success !== false,
    status: String(status).toLowerCase() === 'active' ? 'active' : status,
    phase,
    CURRENT_PHASE: phase || data.CURRENT_PHASE,
    goal,
    BIG_PICTURE: data.BIG_PICTURE || goal,
    ACTIVE_MISSION: data.ACTIVE_MISSION || goal,
    NEXT_ACTION: data.NEXT_ACTION || next || data.next_action || null,
    DO_NOT_TOUCH_NOW: data.DO_NOT_TOUCH_NOW || data.lockdown_related_fields?.DO_NOT_TOUCH_NOW || [],
    TASKS_NOW: data.TASKS_NOW || data.tasks_now || [],
  };
};

export const executeCommand = async (command, isBackground = false, project = 'system', dir = './') => {
  try {
    const response = await fetch(`${API_BASE_URL}/command`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ command, isBackground, project, dir }),
    });
    const data = await response.json();
    return response.ok ? { success: true, ...data } : { success: false, error: data.error || 'Unknown error' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const killProcess = async (pid) => {
  try {
    const response = await fetch(`${API_BASE_URL}/kill`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pid }),
    });
    const data = await response.json();
    return response.ok ? { success: true, ...data } : { success: false, error: data.error || 'Unknown error' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const killAllProcesses = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/kill-all`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
    const data = await response.json();
    return response.ok ? { success: true, ...data } : { success: false, error: data.error || 'Unknown error' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const fetchProcesses = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/processes`);
    const data = await response.json();
    return response.ok ? { success: true, processes: data.processes } : { success: false, error: data.error || 'Unknown error' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const fetchProjects = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/projects`);
    const data = await response.json();
    return response.ok
      ? { success: true, activeProject: data.activeProject, allProjects: data.allProjects }
      : { success: false, error: data.error || 'Unknown error' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const switchProject = async (projectName) => {
  try {
    const response = await fetch(`${API_BASE_URL}/switch-project`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectName }),
    });
    const data = await response.json();
    return response.ok ? { success: true, ...data } : { success: false, error: data.error || 'Unknown error' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const repoAction = async (projectName, action) => {
  try {
    const response = await fetch(`${API_BASE_URL}/repo-action`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectName, action }),
    });
    const data = await response.json();
    return response.ok ? { success: true, ...data } : { success: false, error: data.error || 'Unknown error' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const checkWrangler = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/check-wrangler`);
    const data = await response.json();
    return response.ok ? { success: true, ...data } : { success: false, error: data.error || 'Unknown error' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const installWrangler = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/install-wrangler`, { method: 'POST' });
    const data = await response.json();
    return response.ok ? { success: true, ...data } : { success: false, error: data.error || 'Unknown error' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const generateReport = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/generate-report`, { method: 'POST' });
    const data = await response.json();
    return response.ok ? { success: true, ...data } : { success: false, error: data.error || 'Unknown error' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const downloadReport = async () => {
  try {
    window.open(`${API_BASE_URL}/download-report`, '_blank');
    return { success: true, message: 'Attempting to download report...' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const checkUrls = async (urls) => {
  try {
    const response = await fetch(`${API_BASE_URL}/check-urls`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ urls }),
    });
    const data = await response.json();
    return response.ok ? { success: true, ...data } : { success: false, error: data.error || 'Unknown error' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const fetchLatestCheckpoint = async () => {
  try {
    const response = await fetchFresh(`${API_BASE_URL}/latest`);
    const parsed = await safeJson(response);
    if (!parsed.ok) return { success: false, code: parsed.code, error: parsed.error };
    return response.ok ? { success: true, ...parsed.data } : { success: false, error: parsed.data?.error || 'Unknown error' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const fetchProjectStatus = async () => {
  try {
    const response = await fetchFresh(`${API_BASE_URL}/project-status`);
    const parsed = await safeJson(response);
    if (!parsed.ok) return { success: false, code: parsed.code, error: parsed.error };
    const data = parsed.data || {};
    if (!response.ok) return { success: false, error: data.error || 'Unknown error' };
    return {
      success: true,
      ...data,
      status: data.status || data.health || 'active',
      phase: data.phase || data.current_phase || null,
      active_mission: data.active_mission || data.goal || null,
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const fetchMcpBrokerStatus = async () => {
  try {
    const response = await fetchFresh(`${API_BASE_URL}/mcp-broker-status`);
    const parsed = await safeJson(response);
    if (!parsed.ok) return { success: false, code: parsed.code, error: parsed.error };
    return response.ok ? { success: true, ...parsed.data } : { success: false, error: parsed.data?.error || 'Unknown error' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

const getBackroomBase = () => '/api/backroom';
const TELEPORTE_BACKROOM_BASE = '/api/backroom';

export const normalizeCurrentState = (data = {}) => {
  const current = data.current_state || data;
  return {
    ...data,
    ...current,
    current_state: data.current_state || current,
    success: data.success !== false && data.ok !== false,
    canonicalAuthority: data.authority || data.canonicalAuthority || 'Teleporte D1 teleporte_current_state singleton',
    dataMode: data.role || data.dataMode || 'D1-canonical-truth',
    projectionWarning:
      data.historical_evidence_note ||
      'Canonical current truth is Teleporte D1; projections are display-only.',
  };
};

const normalizePhase = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/priority 1\s*\/\s*/g, '')
    .replace(/[—–]/g, '-')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const projectionPhase = (route, projection = {}) => {
  if (route === 'mission-board') {
    return (
      projection.CURRENT_PHASE ||
      projection.phase ||
      projection.status_code_signals?.CURRENT_PHASE ||
      null
    );
  }
  if (route === 'project-status') return projection.phase || projection.current_phase || null;
  if (route === 'current-run') {
    return projection.phase || projection.mission?.phase || projection.canonical_current_state?.phase || null;
  }
  return projection.phase || projection.current_phase || projection.CURRENT_PHASE || null;
};

const compareProjection = (route, canonical, projection = {}) => {
  const state = canonical?.current_state || canonical || {};
  const canonicalPhase = state.phase || projection.canonical_phase || null;
  const projectedPhase = projectionPhase(route, projection);
  const projection_drift_detected = Boolean(
    canonicalPhase && projectedPhase && normalizePhase(canonicalPhase) !== normalizePhase(projectedPhase)
  );
  return {
    route: `/api/${route}`,
    canonical_revision: state.revision ?? projection.canonical_revision ?? null,
    canonical_phase: canonicalPhase,
    canonical_health: state.health || projection.canonical_health || null,
    projection_phase: projectedPhase,
    projection_updated_at: projection.updatedAt || projection.updated_at || null,
    projection_drift_detected,
    safe_to_use_projection_as_current: !projection_drift_detected,
  };
};

export const fetchCurrentState = async () => {
  try {
    const response = await fetchFresh(`${API_BASE_URL}/state/current`);
    const parsed = await safeJson(response);
    if (!parsed.ok) return { success: false, code: parsed.code, error: parsed.error };
    return response.ok ? normalizeCurrentState(parsed.data) : { success: false, error: parsed.data?.error || 'Unknown error' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const fetchSurfaceConsistency = async () => {
  try {
    const response = await fetchFresh(`${API_BASE_URL}/surface-consistency`);
    const parsed = await safeJson(response);
    if (!parsed.ok) return { success: false, code: parsed.code, error: parsed.error };
    return response.ok
      ? { success: true, ...parsed.data }
      : { success: false, error: parsed.data?.error || 'Unknown error', ...parsed.data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const fetchContinuityArrival = async () => {
  try {
    const response = await fetchFresh(`${API_BASE_URL}/continuity/arrival`);
    const parsed = await safeJson(response);
    if (!parsed.ok) return { success: false, code: parsed.code, error: parsed.error };
    return response.ok
      ? { success: true, ...parsed.data }
      : { success: false, error: parsed.data?.error || 'Unknown error', ...parsed.data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const fetchPhase2RuntimeStatus = async () => {
  try {
    const response = await fetchFresh(`${API_BASE_URL}/phase2/runtime/status`);
    const parsed = await safeJson(response);
    if (!parsed.ok) return { success: false, code: parsed.code, error: parsed.error };
    return response.ok ? { success: true, ...parsed.data } : { success: false, error: parsed.data?.error || 'Unknown error', ...parsed.data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const fetchTeleporteMissionOS = async () => {
  try {
    const [
      currentState,
      phase2Runtime,
      currentRunRaw,
      sourceTrace,
      decisions,
      events,
      contracts,
      validate,
      sourceCloud,
      missionBoardRaw,
      projectStatus,
      arrivalPacket,
      truthRail,
      agentBoot,
      resume,
      chatGptBridge,
      surfaceConsistency,
      continuity,
    ] = await Promise.all([
      fetchCurrentState(),
      fetchPhase2RuntimeStatus(),
      fetchFresh(`${API_BASE_URL}/current-run`).then(async (r) => {
        const p = await safeJson(r);
        return p.ok ? p.data : {};
      }).catch(() => ({})),
      fetchFresh(`${API_BASE_URL}/source-trace`).then(async (r) => {
        const p = await safeJson(r);
        return p.ok ? p.data : {};
      }).catch(() => ({})),
      fetchFresh(`${API_BASE_URL}/decisions`).then(async (r) => {
        const p = await safeJson(r);
        return p.ok ? p.data : {};
      }).catch(() => ({})),
      fetchFresh(`${API_BASE_URL}/events`).then(async (r) => {
        const p = await safeJson(r);
        return p.ok ? p.data : {};
      }).catch(() => ({})),
      fetchFresh(`${API_BASE_URL}/agent-contracts`).then(async (r) => {
        const p = await safeJson(r);
        return p.ok ? p.data : {};
      }).catch(() => ({})),
      fetchFresh(`${API_BASE_URL}/validate`).then(async (r) => {
        const p = await safeJson(r);
        return p.ok ? p.data : {};
      }).catch(() => ({})),
      fetchFresh(`${API_BASE_URL}/source-cloud`).then(async (r) => {
        const p = await safeJson(r);
        return p.ok ? p.data : {};
      }).catch(() => ({})),
      fetchFresh(`${API_BASE_URL}/mission-board`).then(async (r) => {
        const p = await safeJson(r);
        return p.ok ? p.data : {};
      }).catch(() => ({})),
      fetchFresh(`${API_BASE_URL}/project-status`).then(async (r) => {
        const p = await safeJson(r);
        return p.ok ? p.data : {};
      }).catch(() => ({})),
      fetchFresh(`${API_BASE_URL}/arrival-packet`).then(async (r) => {
        const p = await safeJson(r);
        return p.ok ? p.data : {};
      }).catch(() => ({})),
      fetchFresh(`${API_BASE_URL}/truth-rail`).then(async (r) => {
        const p = await safeJson(r);
        return p.ok ? p.data : {};
      }).catch(() => ({})),
      fetchFresh(`${API_BASE_URL}/agent-boot`).then(async (r) => {
        const p = await safeJson(r);
        if (p?.ok === false && p?.verdict !== 'PASS') return {};
        return p.data || p;
      }).catch(() => ({})),
      fetchFresh(`${API_BASE_URL}/resume`).then(async (r) => {
        const p = await safeJson(r);
        if (p?.ok === false && p?.verdict !== 'PASS') return {};
        return p.data || p;
      }).catch(() => ({})),
      fetchFresh(`${API_BASE_URL}/chatgpt-bridge`).then(async (r) => {
        const p = await safeJson(r);
        return p.ok ? p.data : {};
      }).catch(() => ({})),
      fetchSurfaceConsistency(),
      fetchContinuityArrival(),
    ]);

    const currentRun = normalizeCurrentRun(currentRunRaw);
    const missionBoard = normalizeMissionBoard(missionBoardRaw);
    const projectionDrift = [
      compareProjection('current-run', currentState, currentRunRaw),
      compareProjection('project-status', currentState, projectStatus),
      compareProjection('mission-board', currentState, missionBoard),
    ];
    const canonicalState = currentState?.current_state || currentState || {};
    const routeStatus = deriveRouteLabel({
      validateStatus: validate?.status || validate?.verdict || '',
      continuityStatus: continuity?.continuity_status || continuity?.status || '',
      d1Ok: currentState?.success !== false && canonicalState?.revision != null,
      checkpointsPassed: checkpointsPassed(validate?.routes || validate?.checkpoints),
    });

    if (!currentRun.phase && canonicalState.phase) currentRun.phase = canonicalState.phase;
    if (currentRun.revision == null && canonicalState.revision != null) {
      currentRun.revision = canonicalState.revision;
      currentRun.canonical_revision = canonicalState.revision;
    }
    if (!currentRun.next_action && canonicalState.next_action) {
      currentRun.next_action = canonicalState.next_action;
    }
    if (!currentRun.goal && missionBoard.goal) currentRun.goal = missionBoard.goal;
    if (!currentRun.objective && currentRun.goal) currentRun.objective = currentRun.goal;
    if (!currentRun.status && missionBoard.status) currentRun.status = missionBoard.status;

    return {
      success: currentState.success !== false,
      currentState: { ...currentState, ...canonicalState },
      phase2Runtime,
      currentRun,
      sourceTrace,
      decisions,
      events,
      contracts,
      validate,
      sourceCloud,
      missionBoard,
      projectStatus: {
        ...projectStatus,
        success: projectStatus && projectStatus.error ? false : true,
        status: projectStatus.status || currentRun.status || missionBoard.status || 'active',
        phase: projectStatus.phase || currentRun.phase || canonicalState.phase || null,
        active_mission: projectStatus.active_mission || currentRun.goal || missionBoard.goal || null,
      },
      arrivalPacket,
      truthRail,
      agentBoot,
      resume,
      chatGptBridge,
      surfaceConsistency,
      continuity,
      routeStatus,
      projectionDrift,
      projectionDriftDetected: projectionDrift.some((item) => item.projection_drift_detected),
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/** T9: never throw on HTML/SPA; degrade to not_loaded. <!-- locus: locus-backroom --> */
export const fetchBackroomMessages = async (channel, limit = null) => {
  try {
    const query = limit ? `?limit=${limit}` : '';
    const response = await fetchFresh(`${getBackroomBase()}/${channel}/messages${query}`);
    const parsed = await safeJson(response);
    if (!parsed.ok) {
      return {
        success: false,
        code: parsed.code || 'not_loaded',
        error: parsed.error || 'Backroom not loaded',
        messages: [],
      };
    }
    const data = parsed.data;
    const messages = Array.isArray(data)
      ? data
      : Array.isArray(data?.messages)
        ? data.messages
        : [];
    if (!response.ok) {
      return {
        success: false,
        code: data?.code || (response.status === 401 || response.status === 403 ? 'locked' : 'not_loaded'),
        error: data?.error || `HTTP ${response.status}`,
        messages: [],
      };
    }
    return { success: true, messages, code: null };
  } catch (error) {
    const msg = error?.message || String(error);
    return {
      success: false,
      code: 'network',
      error: msg,
      messages: [],
    };
  }
};

/** Session-gated cockpit write. Pages/express forward this to Teleporte v2.5 /envelopes. */
export const postBackroomMessage = async (channel, sender, content) => {
  try {
    const text = String(content || '');
    const idempotencyKey = await stableIdempotencyKey([channel, sender || 'Jarvis', text].join('\n'));
    const response = await fetchFresh('/api/backroom-v2-write', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({
        operation: 'envelope',
        channel,
        idempotency_key: idempotencyKey,
        message: coordinationEnvelope({
          content: text,
          source_chat: sender || 'Jarvis',
        }),
      }),
    });
    const parsed = await safeJson(response);
    if (!parsed.ok) {
      return { success: false, code: parsed.code || 'not_loaded', error: parsed.error };
    }
    if (!response.ok) {
      return {
        success: false,
        code: parsed.data?.code || 'error',
        error: parsed.data?.error || `HTTP ${response.status}`,
      };
    }
    return { success: true, ...parsed.data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const postBackroomMessageDisabled = postBackroomMessage;

/** Multi-agent lane board. Same-origin Pages proxy — do not name upstream in the browser. */
const LANE_API = '/api';

export const fetchLaneBoard = async () => {
  try {
    const response = await fetchFresh(`${LANE_API}/agent-lanes`);
    const parsed = await safeJson(response);
    if (!parsed.ok) return { success: false, code: parsed.code, error: parsed.error };
    return { success: true, ...parsed.data };
  } catch (e) {
    return { success: false, error: e.message || String(e) };
  }
};

export const postLaneCheckin = async (payload) => {
  try {
    const response = await fetch(`${LANE_API}/agent-lanes/checkin`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(payload || {}),
    });
    const parsed = await safeJson(response);
    if (!parsed.ok) return { success: false, code: parsed.code, error: parsed.error };
    return { success: response.ok, ...parsed.data };
  } catch (e) {
    return { success: false, error: e.message || String(e) };
  }
};


export const fetchChatGptBridge = async () => {
  try {
    const response = await fetchFresh(`${API_BASE_URL}/chatgpt-bridge`);
    const parsed = await safeJson(response);
    if (!parsed.ok) return { success: false, code: parsed.code, error: parsed.error };
    return response.ok ? { success: true, ...parsed.data } : { success: false, error: parsed.data?.error || 'Unknown error' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const postChatGptBridgeWrite = async (packet) => {
  try {
    const response = await fetch(`${API_BASE_URL}/chatgpt-bridge/write`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(packet),
    });
    const parsed = await safeJson(response);
    if (!parsed.ok) return { success: false, code: parsed.code, error: parsed.error };
    return response.ok ? { success: true, ...parsed.data } : { success: false, error: parsed.data?.error || 'Unknown error' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const fetchAccessMesh = async () => {
  try {
    const response = await fetchFresh(`${API_BASE_URL}/access-mesh`);
    const parsed = await safeJson(response);
    if (!parsed.ok) {
      return {
        success: true,
        status: 'PUBLIC_COCKPIT_READ_ONLY',
        STRICT_PUBLIC_BOUNDARY: true,
        note: 'access-mesh unavailable; public cockpit continues; Teleporte remains canonical.',
        error: parsed.error,
      };
    }
    if (response.ok) return { success: true, ...parsed.data };
    return {
      success: true,
      status: 'PUBLIC_COCKPIT_READ_ONLY',
      STRICT_PUBLIC_BOUNDARY: true,
      note: 'access-mesh unavailable; public cockpit continues; Teleporte remains canonical.',
      error: parsed.data?.error || 'Unknown error',
    };
  } catch (error) {
    return {
      success: true,
      status: 'PUBLIC_COCKPIT_READ_ONLY',
      STRICT_PUBLIC_BOUNDARY: true,
      note: 'access-mesh unreachable; not a Teleporte mission failure.',
      error: error.message,
    };
  }
};

export const fetchOperatorAlerts = async () => {
  try {
    const response = await fetchFresh(`${API_BASE_URL}/operator-alerts`);
    const parsed = await safeJson(response);
    if (!parsed.ok) return { success: false, code: parsed.code, error: parsed.error };
    if (response.ok) return { success: true, ...parsed.data };
    return { success: false, error: parsed.data?.error || 'Unknown error' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/** Builder Station edge snapshot: queue, sentinels, findings projection (honest not-loaded). */
export const fetchBuilderLive = async () => {
  try {
    const response = await fetchFresh(`${API_BASE_URL}/builder/live`);
    const parsed = await safeJson(response);
    if (!parsed.ok) return { success: false, code: parsed.code, error: parsed.error };
    if (!response.ok) {
      return { success: false, error: parsed.data?.error || parsed.data?.message || `HTTP ${response.status}` };
    }
    return { success: true, ...parsed.data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/** Canonical Builder state: live Teleporte state/current plus mission-board consistency. */
export const fetchBuilderState = async () => {
  try {
    const response = await fetchFresh(`${API_BASE_URL}/builder/state`);
    const parsed = await safeJson(response);
    if (!parsed.ok) return { success: false, code: parsed.code, error: parsed.error };
    if (!response.ok) return { success: false, ...parsed.data, error: parsed.data?.error || `HTTP ${response.status}` };
    return { success: true, ...parsed.data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/** Builder conversation: explain and preview first; local confirmed actions use a separate route. */
export const postBuilderChat = async (message, commandToken = '') => {
  try {
    const response = await fetch(`${API_BASE_URL}/builder/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(commandToken ? { 'x-jarvis-command-token': commandToken } : {}),
      },
      body: JSON.stringify({ message }),
    });
    const parsed = await safeJson(response);
    if (!parsed.ok) return { success: false, code: parsed.code, error: parsed.error };
    if (!response.ok) return { success: false, ...parsed.data, error: parsed.data?.error || `HTTP ${response.status}` };
    return { success: true, ...parsed.data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/** Execute one already-previewed Builder chat action with explicit local authorization. */
export const executeBuilderChatAction = async (actionId, commandToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/builder/chat/execute`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-jarvis-command-token': commandToken,
      },
      body: JSON.stringify({ action_id: actionId, confirmation: 'TONY_APPROVED' }),
    });
    const parsed = await safeJson(response);
    if (!parsed.ok) return { success: false, code: parsed.code, error: parsed.error };
    if (!response.ok) return { success: false, ...parsed.data, error: parsed.data?.error || `HTTP ${response.status}` };
    return { success: true, ...parsed.data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};
